import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from 'components/Panel';
import Break from 'components/Break';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelButtonRow,
  FullPanel,
  PanelContent
} from 'components/PanelComponents';
import {
  BlueButtonWithWriteAccess as AddButton,
  BlackBorderButtonWithWriteAccess as EditButton,
  BlueBorderButton
} from 'components/Button';
import AddRelationsContainer from 'components/Panels/AddRelationsContainer';
import {
  closeSidePanel,
  revertSidePanel,
  setSidePanel
} from 'reducers/sidePanel/actions';
import { RESOURCE_TYPES, SIDEPANEL_STATES } from 'utils/appConstants';
import { determineBack } from 'utils/determineBack';
import {
  POLL_DEVICES,
  POLL_INDIVIDUAL_AGGREGATOR,
  subscribe,
  unsubscribe
} from 'reducers/polling/actions';
import Table from 'components/Table';

const deviceColumnDefs = [
  {
    headerName: 'ID',
    field: 'deviceId'
  },
  {
    headerName: 'LFDI',
    field: 'lfdi'
  },
  {
    headerName: 'Registration Pin',
    field: 'registrationPin'
  }
];

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);
const subName = 'AggregatorPanel';

const AggregatorPanel = ({ isActive }) => {
  const dispatch = useDispatch();
  const devices = useSelector(state => state.device.devices);
  const sidePanel = useSelector(state => state.sidePanel);
  const aggregators = useSelector(state => state.aggregator.aggregators);
  const [isLoading, setIsLoading] = useState(true);
  const [aggregator, setAggregator] = useState({});
  const [nodeDevices, setNodeDevices] = useState([]);

  const compileAggregatorInfo = useCallback(() => {
    const { id } = sidePanel;

    const aggregator = aggregators.find(
      aggregator => aggregator.aggregatorId === id
    );

    const nodeDevices = devices.filter(device => device.aggregatorId === id);
    setAggregator(aggregator);
    setNodeDevices(nodeDevices);
  }, [aggregators, devices, sidePanel]);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: subName,
          pollTypes: [
            {
              type: POLL_INDIVIDUAL_AGGREGATOR,
              resourceId: sidePanel.id
            },
            {
              type: POLL_DEVICES
            }
          ],
          pollRate
        })
      );
      compileAggregatorInfo();
    } else {
      dispatch(unsubscribe(subName));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(subName));
      }
    };
  }, [dispatch, compileAggregatorInfo, sidePanel.id, isActive]);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, aggregator]);

  const onDeviceSelected = useCallback(
    row => {
      const { sfdi } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.DEVICE,
          state: SIDEPANEL_STATES.READ,
          sfdi,
          keepBreadcrumbs: true
        })
      );
    },
    [dispatch]
  );

  if (!isActive || (isActive && isLoading) || aggregator === undefined) {
    return null;
  } else {
    const { aggregatorId, lfdi, sfdi, registrationPin } = aggregator;
    return (
      <FullPanel>
        <Panel title="Selected Aggregator Info" primaryHeader />
        <PanelContent>
          <SubPanel>
            <SubPanelRow>
              <PanelRowDescriptor alignSelf="flex-end">ID</PanelRowDescriptor>
              <PanelRowDisplay alignSelf="flex-end">
                {aggregatorId}
              </PanelRowDisplay>
              <EditButton
                onClick={() =>
                  dispatch(
                    setSidePanel({
                      entity: RESOURCE_TYPES.AGGREGATOR,
                      state: SIDEPANEL_STATES.UPDATE,
                      id: aggregatorId,
                      keepBreadcrumbs: true
                    })
                  )
                }
              >
                Edit
              </EditButton>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>LFDI</PanelRowDescriptor>
              <PanelRowDisplay>{lfdi}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>SFDI</PanelRowDescriptor>
              <PanelRowDisplay>{sfdi}</PanelRowDisplay>
            </SubPanelRow>
            <SubPanelRow>
              <PanelRowDescriptor>Registration Pin</PanelRowDescriptor>
              <PanelRowDisplay>{registrationPin}</PanelRowDisplay>
            </SubPanelRow>
          </SubPanel>

          <Panel title="Associated Devices">
            <SubPanel>
              <Table
                rowData={nodeDevices}
                columnDefs={deviceColumnDefs}
                height="200px"
                width="100%"
                padding="0px"
                margin="10px 0px"
                onRowClicked={onDeviceSelected}
              />
            </SubPanel>

            <Break margin="8px 0" />

            <SubPanel>
              <AddRelationsContainer title="Add Associated Device">
                <AddButton
                  buttonWidth="140px"
                  margin="0 4px 0 0"
                  onClick={() =>
                    dispatch(
                      setSidePanel({
                        entity: RESOURCE_TYPES.DEVICE,
                        state: SIDEPANEL_STATES.ADD_EXISTING,
                        keepBreadcrumbs: true,
                        data: { parentAggregatorId: aggregatorId }
                      })
                    )
                  }
                >
                  Find Existing Device
                </AddButton>
                <AddButton
                  buttonWidth="100px"
                  margin="0 0 0 4px"
                  onClick={() =>
                    dispatch(
                      setSidePanel({
                        entity: RESOURCE_TYPES.DEVICE,
                        state: SIDEPANEL_STATES.CREATE,
                        keepBreadcrumbs: true,
                        data: { parentAggregatorId: aggregatorId }
                      })
                    )
                  }
                >
                  + New Device
                </AddButton>
              </AddRelationsContainer>
            </SubPanel>
          </Panel>
        </PanelContent>
        <PanelButtonRow>
          <BlueBorderButton
            onClick={() =>
              determineBack(
                sidePanel,
                () => dispatch(revertSidePanel()),
                () => dispatch(closeSidePanel())
              )
            }
          >
            Back
          </BlueBorderButton>
        </PanelButtonRow>
      </FullPanel>
    );
  }
};

export default AggregatorPanel;
